import { createSDKAction, onFulfill, onPending, onReject } from '../util';
import socket from '../../sdk/services/socket';
import { createAuthorizationHeader } from '../../sdk/utils';
import ActionTypes from '../action-types';
import { ERROR_MESSAGES } from '../../../Constants';
import { store } from '../index';

function isProfileIncomplete(profile) {
  if (!profile.birthday || !profile.gender || !profile.countryOfLiving) {
    return true;
  }

  const hasEmptyRegion = profile.languages.find(item => !item.dialect);
  return !!hasEmptyRegion;
}

export function getMetrics(accessToken = null) {
  const { me } = store.getState();
  const { metricsDuration } = me;

  return createSDKAction(ActionTypes.GET_AGENT_METRICS, {
    callback: (sdk, dispatch) => {
      dispatch({ type: onPending(ActionTypes.GET_AGENT_METRICS) });
      const headers = accessToken ? { ...createAuthorizationHeader(accessToken) } : {};
      return sdk
        .api('agent/metrics', {
          requiresAuthentication: !accessToken,
          method: 'get',
          headers,
          isSingularEndpoint: true,
          queryParams: { metricsDuration }
        })
        .then(response => {
          const payload = response.body;
          dispatch({ type: onFulfill(ActionTypes.GET_AGENT_METRICS), payload });
          return Promise.resolve(payload);
        })
        .catch(err => {
          dispatch({ type: onReject(ActionTypes.GET_AGENT_METRICS) });
          return Promise.reject(err);
        });
    }
  });
}

export function getProfile(accessToken = null) {
  return createSDKAction(ActionTypes.GET_AGENT_PROFILE, {
    callback: (sdk, dispatch) => {
      dispatch({ type: onPending(ActionTypes.GET_AGENT_PROFILE) });
      const headers = accessToken ? { ...createAuthorizationHeader(accessToken) } : {};
      return sdk
        .api('/agent/profile', {
          requiresAuthentication: !accessToken,
          method: 'get',
          headers,
          isSingularEndpoint: true
        })
        .then(response => {
          const payload = response.body;
          dispatch({ type: onFulfill(ActionTypes.GET_AGENT_PROFILE), payload });
          return Promise.resolve(payload);
        })
        .catch(err => {
          dispatch({ type: onReject(ActionTypes.GET_AGENT_PROFILE) });
          return Promise.reject(err);
        });
    }
  });
}
export function login(username, password) {
  return createSDKAction(ActionTypes.LOGIN, {
    callback: async (sdk, dispatch) => {
      dispatch({ type: onPending(ActionTypes.LOGIN) });

      try {
        const { body } = await sdk.api('/account/login', {
          body: {
            password,
            username
          },
          method: 'post'
        });

        const profile = await dispatch(getProfile(body.accessToken));

        if (isProfileIncomplete(profile)) {
          dispatch({ type: ActionTypes.SESSION_OFF });
          dispatch({ type: onReject(ActionTypes.LOGIN), isProfileIncomplete: true });

          return Promise.reject({ reason: ERROR_MESSAGES.PROFILE_INCOMPLETE_EXCEPTION });
        }

        sdk.setToken({ token: body });
        dispatch({ type: ActionTypes.SESSION_ON });
        dispatch({ type: onFulfill(ActionTypes.LOGIN) });
        return profile;
      } catch (e) {
        dispatch({ type: ActionTypes.SESSION_OFF });
        dispatch({ type: onReject(ActionTypes.LOGIN) });

        return Promise.reject({ reason: ERROR_MESSAGES.INVALID_CREDENTIALS_EXCEPTION });
      }
    }
  });
}

export function socialLogin(facebookAccessToken) {
  return createSDKAction(ActionTypes.LOGIN_SOCIAL, {
    callback: (sdk, dispatch) => {
      dispatch({ type: onPending(ActionTypes.LOGIN_SOCIAL) });

      return sdk
        .api('/account/login/facebook', {
          body: {
            facebookAccessToken
          },
          method: 'post'
        })
        .then(({ body }) => {
          sdk.setToken({ token: body });

          dispatch({ type: ActionTypes.SESSION_ON });
          dispatch({ type: onFulfill(ActionTypes.LOGIN_SOCIAL) });
        })
        .catch(response => {
          dispatch({ type: ActionTypes.SESSION_OFF });
          dispatch({ type: onReject(ActionTypes.LOGIN_SOCIAL) });

          return Promise.reject(response);
        });
    }
  });
}

export function refreshSession() {
  return createSDKAction(ActionTypes.SESSION, {
    callback: (sdk, dispatch) => {
      dispatch({ type: onPending(ActionTypes.SESSION) });

      return sdk
        .getTokens()
        .then(({ refreshToken }) => {
          return sdk
            .api('/account/refresh', {
              body: { refreshToken },
              method: 'post'
            })
            .then(({ body }) => {
              dispatch({ type: onFulfill(ActionTypes.SESSION) });

              return sdk.setToken({ token: body });
            })
            .catch(() => {
              dispatch({ type: ActionTypes.SESSION_OFF });
              dispatch({ type: onReject(ActionTypes.SESSION) });

              return Promise.reject();
            });
        })
        .catch(() => {
          dispatch({ type: ActionTypes.SESSION_OFF });
          dispatch({ type: onReject(ActionTypes.SESSION) });

          return Promise.reject();
        });
    }
  });
}

export function logout(errorMessage) {
  return createSDKAction(ActionTypes.LOGOUT, {
    callback: (sdk, dispatch) => {
      dispatch({ type: onPending(ActionTypes.LOGOUT) });

      return sdk
        .getTokens()
        .then(({ refreshToken }) =>
          sdk
            .api('/account/logout', {
              body: { refreshToken },
              method: 'post'
            })
            .then(() => {
              dispatch({ type: onFulfill(ActionTypes.LOGOUT), errorMessage });
              dispatch({ type: ActionTypes.SESSION_OFF });

              return sdk.removeToken();
            })
            .catch(() => {
              dispatch({ type: ActionTypes.SESSION_OFF });
              dispatch({ type: onReject(ActionTypes.LOGOUT) });

              return Promise.reject();
            })
        )
        .catch(() => {
          dispatch({ type: onReject(ActionTypes.LOGOUT) });

          return Promise.reject();
        });
    }
  });
}

export function setAvailability(availability) {
  return createSDKAction(ActionTypes.SET_AVAILABILITY, {
    callback: (sdk, dispatch) => {
      dispatch({ payload: availability, type: ActionTypes.SET_AVAILABILITY });

      return Promise.resolve(availability);
    }
  });
}

export function getJobAgentSow(jobId) {
  return createSDKAction(ActionTypes.GET_JOB_AGENT_SOW, {
    callback: (sdk, dispatch) => {
      dispatch({ type: onPending(ActionTypes.GET_JOB_AGENT_SOW) });
      return sdk
        .api(`/sow/${jobId}`, {
          requiresAuthentication: true,
          method: 'get'
        })
        .then(response => {
          const { sowId, isSigned, isUpdated, agentSowJson, jobCode, jobTypeName } = response.body;
          const payload = {
            jobId,
            sowId,
            isSigned,
            isUpdated,
            jobCode,
            jobTypeName,
            agentSowJson: agentSowJson ? JSON.parse(agentSowJson) : null
          };
          dispatch({ type: onFulfill(ActionTypes.GET_JOB_AGENT_SOW), payload });

          return Promise.resolve(payload);
        })
        .catch(() => {
          dispatch({ type: onReject(ActionTypes.GET_JOB_AGENT_SOW) });
        });
    }
  });
}

export function getJobAgentHits(guidId, isTaskId = false) {
  return createSDKAction(ActionTypes.GET_JOB_AGENT_HITS, {
    callback: (sdk, dispatch) => {
      dispatch({ type: onPending(ActionTypes.GET_JOB_AGENT_HITS) });
      const url = isTaskId ? `/hits/task/${guidId}` : `/hits/${guidId}`;
      return sdk
        .api(url, {
          requiresAuthentication: true,
          method: 'get'
        })
        .then(response => {
          const { executedHitsCount, maxHitsCount } = response.body;
          const payload = {
            executedHitsCount,
            maxHitsCount
          };
          dispatch({ type: onFulfill(ActionTypes.GET_JOB_AGENT_HITS), payload });

          return Promise.resolve(payload);
        })
        .catch(() => {
          dispatch({ type: onReject(ActionTypes.GET_JOB_AGENT_HITS) });
        });
    }
  });
}

export function getIsUserVendor() {
  return createSDKAction(ActionTypes.GET_AGENT_IS_VENDOR, {
    callback: (sdk, dispatch) => {
      dispatch({ type: onPending(ActionTypes.GET_AGENT_IS_VENDOR) });
      return sdk
        .api(`/agent/isVendor`, {
          requiresAuthentication: true,
          method: 'get'
        })
        .then(response => {
          const payload = response.body;
          dispatch({ type: onFulfill(ActionTypes.GET_AGENT_IS_VENDOR), payload });

          return payload;
        })
        .catch(() => {
          dispatch({ type: onReject(ActionTypes.GET_AGENT_IS_VENDOR) });
        });
    }
  });
}

export function signJobAgentSow(jobId, sowId) {
  return createSDKAction(ActionTypes.SIGN_JOB_AGENT_SOW, {
    callback: (sdk, dispatch) => {
      dispatch({ type: onPending(ActionTypes.SIGN_JOB_AGENT_SOW) });

      return sdk
        .api(`/sow/${jobId}/${sowId}`, {
          requiresAuthentication: true,
          method: 'post'
        })
        .then(response => {
          dispatch({ type: onFulfill(ActionTypes.SIGN_JOB_AGENT_SOW) });

          return Promise.resolve(response);
        })
        .catch(err => {
          dispatch({ type: onReject(ActionTypes.SIGN_JOB_AGENT_SOW) });

          return Promise.reject(err);
        });
    }
  });
}

export function getTOUAcceptance() {
  return createSDKAction(ActionTypes.GET_AGENT_TOU_ACCEPTANCE, {
    callback: (sdk, dispatch) => {
      dispatch({ type: onPending(ActionTypes.GET_AGENT_TOU_ACCEPTANCE) });
      return sdk
        .api('/termsofuse', {
          requiresAuthentication: true,
          method: 'get',
          isSingularEndpoint: true
        })
        .then(response => {
          if (response.status !== 200) {
            return Promise.reject(response);
          }
          const { agreementDate: payload } = response.body;
          dispatch({ type: onFulfill(ActionTypes.GET_AGENT_TOU_ACCEPTANCE), payload });
          return Promise.resolve(payload);
        })
        .catch(err => {
          dispatch({ type: onReject(ActionTypes.GET_AGENT_TOU_ACCEPTANCE) });
          return Promise.reject(err);
        });
    }
  });
}

export function signTOUAcceptance() {
  return createSDKAction(ActionTypes.SIGN_AGENT_TOU_ACCEPTANCE, {
    callback: (sdk, dispatch) => {
      return sdk
        .api('/termsofuse', {
          requiresAuthentication: true,
          method: 'post',
          isSingularEndpoint: true
        })
        .then(response => {
          if (response.status !== 200) {
            return Promise.reject(response);
          }
          const { agreementDate: payload } = response.body;
          dispatch({ type: ActionTypes.SIGN_AGENT_TOU_ACCEPTANCE, payload });
          return Promise.resolve(payload);
        })
        .catch(err => {
          return Promise.reject(err);
        });
    }
  });
}

export function savePhoneNumber(phoneNumber) {
  return createSDKAction(ActionTypes.SAVE_AGENT_PHONE_NUMBER, {
    callback: (sdk, dispatch) => {
      dispatch({ type: onPending(ActionTypes.SAVE_AGENT_PHONE_NUMBER) });
      return sdk
        .api('/agent/phoneNumber', {
          requiresAuthentication: true,
          method: 'post',
          body: {
            phoneNumber
          }
        })
        .then(response => {
          dispatch({ type: onFulfill(ActionTypes.SAVE_AGENT_PHONE_NUMBER), phoneNumber });
          return Promise.resolve(response.body);
        })
        .catch(err => {
          dispatch({ type: onReject(ActionTypes.SAVE_AGENT_PHONE_NUMBER) });
          return Promise.reject(err);
        });
    }
  });
}

/**
 * Synchronises a SignalR stream with local Redux
 */
export function liveAgentStatus() {
  return createSDKAction(ActionTypes.LOGOUT, {
    callback: async (sdk, dispatch) => {
      const { accessToken } = await sdk.getTokens();

      const connection = await socket(`${window._env_.API_HUB_URL}/agent`, accessToken);

      connection.on('disableAgent', () =>
        dispatch(logout(ERROR_MESSAGES.USER_STATUS_HAS_CHANGED_EXCEPTION))
      );

      connection.onreconnected(() => {
        window.location.reload();
      });

      await connection;
      return connection;
    }
  });
}

export function changeMetricsDuration(metricsDuration) {
  return dispatch => {
    dispatch({ type: ActionTypes.SET_METRICS_DURATION, metricsDuration });
  };
}
